import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import { LoadingBlock, EmptyState } from '../components/States';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Package, Plus, Pencil, Trash2, Search, Star } from 'lucide-react';
import { toast } from 'sonner';

import { errMsg } from '../lib/errors';
export default function Products() {
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [q, setQ] = useState('');
    const [categoryId, setCategoryId] = useState('all');
    const [brandId, setBrandId] = useState('all');
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const params = { limit: 200 };
            if (q) params.q = q;
            if (categoryId !== 'all') params.category_id = categoryId;
            if (brandId !== 'all') params.brand_id = brandId;
            const [pr, cr, br] = await Promise.all([
                api.get('/products', { params }),
                api.get('/categories'),
                api.get('/brands'),
            ]);
            setItems(pr.data.items || []);
            setCategories(cr.data);
            setBrands(br.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [categoryId, brandId]);

    const doDelete = async (p) => {
        if (!window.confirm(`Delete product "${p.name}"?`)) return;
        try { await api.delete(`/products/${p.id}`); toast.success('Deleted'); load(); }
        catch (e) { toast.error(errMsg(e, 'Delete failed')); }
    };

    const catName = (id) => categories.find(c => c.id === id)?.name || '—';
    const brandName = (id) => brands.find(b => b.id === id)?.name || '—';

    return (
        <AppShell pageTitle="Products" pageActions={
            <Button onClick={() => nav('/dharmikdashboard/products/new')} data-testid="add-product-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>
                <Plus className="h-4 w-4 mr-2" />Add Product
            </Button>
        }>
            <Card className="p-3 sm:p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="relative md:col-span-2">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                        <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') load(); }} placeholder="Search by name or SKU" className="pl-9" data-testid="product-search-input" />
                    </div>
                    <Select value={categoryId} onValueChange={setCategoryId}>
                        <SelectTrigger data-testid="products-filter-category-select"><SelectValue placeholder="Category" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All categories</SelectItem>
                            {categories.map(c => <SelectItem key={c.id} value={c.id}>{'—'.repeat(c.level)} {c.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={brandId} onValueChange={setBrandId}>
                        <SelectTrigger><SelectValue placeholder="Brand" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All brands</SelectItem>
                            {brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </Card>

            {loading ? <LoadingBlock /> : items.length === 0 ? (
                <EmptyState icon={Package} title="No products found" description="Add products to start selling."
                    action={<Button onClick={() => nav('/dharmikdashboard/products/new')}><Plus className="h-4 w-4 mr-2" />Add Product</Button>} />
            ) : (
                <Card className="p-0 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="products-table">
                        <thead>
                            <tr className="border-b bg-neutral-50 text-xs text-neutral-500">
                                <th className="text-left p-3 font-medium">Product</th>
                                <th className="text-left font-medium">SKU</th>
                                <th className="text-left font-medium">Category</th>
                                <th className="text-left font-medium">Brand</th>
                                <th className="text-right font-medium">Retail</th>
                                <th className="text-right font-medium">Wholesale</th>
                                <th className="text-right font-medium">Stock</th>
                                <th className="text-left font-medium">Status</th>
                                <th className="text-right p-3 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(p => (
                                <tr key={p.id} className="border-b hover:bg-neutral-50" data-testid={`product-row-${p.sku}`}>
                                    <td className="p-3">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-md bg-neutral-100 overflow-hidden shrink-0">
                                                {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
                                            </div>
                                            <div>
                                                <div className="font-medium flex items-center gap-1">{p.name}{p.featured && <Star className="h-3 w-3 fill-amber-500 text-amber-500" />}</div>
                                                <div className="text-xs text-neutral-500 truncate max-w-[220px]">{p.short_description}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="font-mono text-xs">{p.sku}</td>
                                    <td className="text-xs">{catName(p.category_id)}</td>
                                    <td className="text-xs">{brandName(p.brand_id)}</td>
                                    <td className="text-right tabular-nums">₹{p.pricing?.retail_selling_price}</td>
                                    <td className="text-right tabular-nums text-neutral-600">₹{p.pricing?.wholesale_selling_price || '-'}</td>
                                    <td className={`text-right tabular-nums ${(p.inventory?.available || 0) <= (p.low_stock_threshold || 5) ? 'text-red-600 font-medium' : ''}`}>{p.inventory?.available || 0}</td>
                                    <td>{p.active ? <span className="text-green-700 text-xs">Active</span> : <span className="text-neutral-500 text-xs">Inactive</span>}</td>
                                    <td className="p-3 text-right whitespace-nowrap">
                                        <Button variant="ghost" size="icon" onClick={() => nav(`/dharmikdashboard/products/${p.id}`)} data-testid={`edit-product-${p.sku}`}><Pencil className="h-4 w-4" /></Button>
                                        <Button variant="ghost" size="icon" onClick={() => doDelete(p)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}
        </AppShell>
    );
}
